import { AgencySubscriptionDto } from "@intelligems/ig-types/dtos/agencies";
import {
  GetLivePricesRequestQueryStringParamsType,
  GetLivePricesResponseBodyType,
} from "@intelligems/ig-types/functions/intelligems/get-live-prices/get-live-prices";
import {
  STRIPE_LATEST_PRODUCT_VERSION,
  StripeProductVersion,
} from "@intelligems/ig-types/schemas/stripe";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { intelligemsKeys } from "../queryKeys";
import type { QueryOptions } from "../../types/react-query";

/**
 * Rejects when the payload has no `prices` array: the schema declares it
 * required and consumers call array methods on it. A resolved empty fallback
 * would be cached as fresh for the whole stale time.
 */
export const getLivePrices = async (
  versions: StripeProductVersion[]
): Promise<GetLivePricesResponseBodyType> => {
  const resp = await api.get<
    Partial<GetLivePricesResponseBodyType> | null,
    GetLivePricesRequestQueryStringParamsType
  >(`/v2/intelligems/prices?versions=${versions.join(",")}`);
  const prices = resp.data?.prices;

  if (!Array.isArray(prices)) {
    throw new Error(
      "GET /v2/intelligems/prices responded without a price list"
    );
  }

  return { prices };
};

export const useCurrentPriceVersion = () => {
  const adminOrg = useGetOrganizationSummary();
  return (
    adminOrg.data.org.stripeSubscriptionSummaries
      .filter(
        (ss) =>
          ss.status === "active" ||
          ss.status === "trialing" ||
          ss.status === "pending"
      )
      .flatMap((os) => os.version)
      .sort()
      .pop()! || STRIPE_LATEST_PRODUCT_VERSION
  );
};

export const useSubscriptionVersion = () => {
  const adminOrg = useGetOrganizationSummary();

  return (
    [
      ...adminOrg.data.org.stripeSubscriptionSummaries.filter(
        (ss) =>
          ss.status === "active" ||
          ss.status === "trialing" ||
          ss.status === "pending"
      ),
      ...adminOrg.data.org.organizationSubscriptions.filter(
        (os) =>
          os.status === "active" ||
          os.status === "trialing" ||
          os.status === "pending"
      ),
    ]
      .flatMap((os) => os.version)
      .sort()
      .pop()! || STRIPE_LATEST_PRODUCT_VERSION
  );
};

//TEMP needed for Billing2026 adoption period
export const useGetLegacyLivePrices = () =>
  useQuery<GetLivePricesResponseBodyType, Error>({
    queryKey: [...intelligemsKeys.livePrices, "v3"],
    queryFn: async () => getLivePrices(["v3"]),
  });

export const useGetLivePrices = () => {
  const version = useCurrentPriceVersion();
  return useQuery<GetLivePricesResponseBodyType, Error>({
    queryKey: intelligemsKeys.livePrices,
    queryFn: async () => getLivePrices([version]),
  });
};

export const stripUiHiddenModularPrices = (
  response: GetLivePricesResponseBodyType
): GetLivePricesResponseBodyType => ({
  ...response,
  prices: response.prices.filter((price) => !price.product.uiHidden),
});

export const useGetModularLivePrices = (
  options?: QueryOptions<GetLivePricesResponseBodyType, Error>
) =>
  useQuery<GetLivePricesResponseBodyType, Error>({
    ...options,
    queryKey: [...intelligemsKeys.livePrices, "2026_module_v1"],
    queryFn: async () =>
      stripUiHiddenModularPrices(await getLivePrices(["2026_module_v1"])),
  });

export const buildAgencyVersions = (
  agencySubscriptions: AgencySubscriptionDto[] | undefined
): StripeProductVersion[] => {
  if (!agencySubscriptions) {
    return [];
  }

  const result = [
    ...new Set([
      "2026_module_v1" as StripeProductVersion,
      "v2" as StripeProductVersion,
      ...agencySubscriptions
        .filter((s) => s.status === "active" || s.status === "trialing")
        .map((s) => s.version),
    ]),
  ] as StripeProductVersion[];

  if (!result.includes("v3")) {
    result.push("v3");
  }

  return result.toSorted();
};

export const useGetAgencyLivePrices = (
  agencySubscriptions: AgencySubscriptionDto[] | undefined
) => {
  const sortedVersions = buildAgencyVersions(agencySubscriptions);

  return useQuery<GetLivePricesResponseBodyType, Error>({
    queryKey: [...intelligemsKeys.livePrices, "agency", sortedVersions],
    queryFn: async () => getLivePrices(sortedVersions),
    enabled: sortedVersions.length > 0,
  });
};
